import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for errors
      const errorParam = searchParams.get('error');
      if (errorParam) {
        setError('Login failed. Please try again.');
        return;
      }

      // Get token from URL
      const token = searchParams.get('token');
      if (!token) {
        setError('No authentication token received.');
        return;
      }

      // Store token securely
      try {
        localStorage.setItem('session_token', token);
        sessionStorage.setItem('session_token', token);
      } catch (e) {
        console.warn('Storage error:', e);
      }

      // Fetch user data with the token
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const userData = await response.json();
          login(userData, token);
          toast.success(`Welcome, ${userData.name}!`);
          
          // Check for redirect URL stored before login (check both storages)
          let redirectUrl = sessionStorage.getItem('redirectAfterLogin') || localStorage.getItem('redirectAfterLogin');
          
          if (redirectUrl) {
            // Clear from both storages
            sessionStorage.removeItem('redirectAfterLogin');
            localStorage.removeItem('redirectAfterLogin');
            navigate(redirectUrl, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          setError('Failed to verify authentication.');
        }
      } catch (err) {
        console.error('Auth verification error:', err);
        setError('Authentication failed. Please try again.');
      }
    };

    processAuth();
  }, [searchParams, navigate, login]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive text-lg mb-2 font-medium">{error}</p>
          <p className="text-muted-foreground text-sm mb-4">
            Please try logging in again.
          </p>
          <button 
            onClick={() => navigate('/login')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg font-heading text-primary">Signing you in...</p>
        <p className="text-muted-foreground mt-2">Please wait</p>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
